import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Spin,
  Empty,
  Typography,
  Descriptions,
  Tag,
  Divider,
} from "antd";
import { useTranslation } from "react-i18next";
import { useFormatos } from "../../../../i18n/formatos";
import { useAuth } from "../../../../hooks";
import { ReviResultsApi } from "../../../../api/revi/reviResultsApi";
import "./TherapistResultDetailModal.scss";

const { Text, Title } = Typography;
const api = new ReviResultsApi();

// El enunciado de la pregunta viene de la BD y NO se traduce; lo unico
// traducible es el texto de relleno cuando no se encuentra la pregunta, y por
// eso la funcion recibe `t`.
function questionText(evaluation, answer, t) {
  const qid = answer?.questionId;
  const qs = Array.isArray(evaluation?.questions) ? evaluation.questions : [];
  const q =
    qs.find((x) => x?.id === qid) ||
    qs.find((x) => String(x?._id || "") === String(qid || "")) ||
    null;
  return q?.text || q?.question || t("resultado.preguntaSinTexto");
}

export function TherapistResultDetailModal({ open, onClose, resultId }) {
  const { t } = useTranslation(["patient", "evaluations", "common"]);
  const { fechaHora } = useFormatos();

  const { accessToken } = useAuth();

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open || !resultId || !accessToken) return;

    (async () => {
      try {
        setLoading(true);
        const res = await api.getResultDetailForTherapist(
          accessToken,
          resultId,
        );
        setResult(res.result || null);
      } catch (e) {
        console.error(e);
        setResult(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, resultId, accessToken]);

  const processed = result?.processedScore || null;

  const dimensions = useMemo(() => {
    const arr = Array.isArray(processed?.dimensions)
      ? processed.dimensions
      : [];
    return arr.filter((d) => d?.name);
  }, [processed]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={920}
      destroyOnHidden
      title={t("resultado.titulo")}
    >
      {loading ? (
        <div className="revi-result-detail__center">
          <Spin size="large" />
        </div>
      ) : !result ? (
        <Empty description={t("resultado.errorCarga")} />
      ) : (
        <div className="revi-result-detail">
          <Title level={4} style={{ margin: 0 }}>
            {result?.evaluation?.title || t("resultado.evaluacionSinTitulo")}
          </Title>

          <Divider style={{ margin: "12px 0" }} />

          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label={t("common:fields.date")} span={2}>
              {fechaHora(result?.sessionDate)}
            </Descriptions.Item>

            <Descriptions.Item label={t("resultado.campos.terapia")} span={2}>
              {result?.therapy?.title || "—"}
            </Descriptions.Item>

            <Descriptions.Item label={t("resultado.campos.subtipo")}>
              {result?.evaluation?.subtype ? (
                <Tag>{result.evaluation.subtype}</Tag>
              ) : (
                "—"
              )}
            </Descriptions.Item>

            <Descriptions.Item label={t("resultado.campos.areaClinica")}>
              {result?.evaluation?.clinicalArea ? (
                <Tag>{result.evaluation.clinicalArea}</Tag>
              ) : (
                "—"
              )}
            </Descriptions.Item>

            <Descriptions.Item label={t("resultado.campos.total")}>
              {typeof processed?.total === "number" ? (
                <Tag>{processed.total}</Tag>
              ) : (
                "—"
              )}
            </Descriptions.Item>

            <Descriptions.Item label={t("resultado.campos.interpretacion")}>
              <Text type="secondary">{processed?.interpretation || "—"}</Text>
            </Descriptions.Item>
          </Descriptions>

          {dimensions.length ? (
            <>
              <Divider style={{ margin: "14px 0" }} />
              <Title level={5} style={{ marginBottom: 10 }}>
                {t("resultado.dimensiones")}
              </Title>
              <div className="revi-result-detail__dims">
                {dimensions.map((d, idx) => (
                  <div
                    key={`${d.name}-${idx}`}
                    className="revi-result-detail__dim"
                  >
                    <Text strong>{d.name}</Text>
                    <Tag>{typeof d.score === "number" ? d.score : "—"}</Tag>
                    {d.level ? <Tag>{d.level}</Tag> : null}
                  </div>
                ))}
              </div>
            </>
          ) : null}

          <Divider style={{ margin: "14px 0" }} />
          <Title level={5} style={{ marginBottom: 10 }}>
            {t("resultado.respuestas")}
          </Title>

          <div className="revi-result-detail__answers">
            {(Array.isArray(result?.answers) ? result.answers : []).length ? (
              (result.answers || []).map((a, idx) => (
                <div
                  key={`${a?.questionId || idx}`}
                  className="revi-result-detail__answer"
                >
                  <Text strong>
                    {t("evaluations:detalle.preguntaNumerada", {
                      numero: idx + 1,
                      enunciado: questionText(result?.evaluation, a, t),
                    })}
                  </Text>
                  <div className="revi-result-detail__value">
                    <Text type="secondary">
                      {t("resultado.respuestaEtiqueta")}
                    </Text>{" "}
                    <Tag>{String(a?.value ?? "—")}</Tag>
                  </div>
                </div>
              ))
            ) : (
              <Empty description={t("resultado.sinRespuestas")} />
            )}
          </div>

          {result?.comments ? (
            <>
              <Divider style={{ margin: "14px 0" }} />
              <Text strong>{t("resultado.comentarios")}</Text>
              <div style={{ marginTop: 6 }}>
                <Text type="secondary">{result.comments}</Text>
              </div>
            </>
          ) : null}

          {result?.patient?.clinical_info?.diagnosis ? (
            <>
              <Divider style={{ margin: "14px 0" }} />
              <Title level={5} style={{ marginBottom: 6 }}>
                {t("resultado.contextoClinico")}
              </Title>
              <div
                style={{
                  padding: "10px 14px",
                  background: "#f6ffed",
                  borderRadius: 8,
                  border: "1px solid #b7eb8f",
                }}
              >
                {result.patient.clinical_info.diagnosis && (
                  <div style={{ marginBottom: 4 }}>
                    <Text strong>{t("resultado.diagnostico")} </Text>
                    <Text>{result.patient.clinical_info.diagnosis}</Text>
                  </div>
                )}
                {result.patient.clinical_info.referral_info && (
                  <div>
                    <Text strong>{t("resultado.referencia")} </Text>
                    <Text>{result.patient.clinical_info.referral_info}</Text>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
